import { IProduct, IProductParams, ImportProduct } from "~/types/dropshipping";
import { sendDelete, sendGet, sendPost, sendPut } from "../base";
import { getAPIData } from "../helpers";
import {
  IAddressParams,
  ICreateSampleOrderParams,
  IOrdersParams,
  IPushToStoreParams,
  IUpdateRetailPriceParams,
  ImportListParams,
  IDropshippingSettings,
  IProductAssetPayload,
  IProductVariantsPayload,
} from "./types";
import { format } from "date-fns";

export const getProducts = (params?: IProductParams) =>
  getAPIData(sendGet("dropshipping/products/", params));

export const getCategories = () =>
  getAPIData(sendGet("dropshipping/categories/"));

export const addToImportList = (product_id: string) =>
  getAPIData(sendPost(`dropshipping/imported-products/${product_id}/`));

export const getImportList = (params?: ImportListParams) =>
  getAPIData(sendGet(`dropshipping/imported-products/`, params));

export const deleteImportedProduct = (product_id: string) =>
  getAPIData(
    sendDelete(`dropshipping/imported-products/${product_id}`, product_id)
  );

export const pushToStore = (params: IPushToStoreParams) =>
  getAPIData(
    sendPut(`dropshipping/imported-products/${params.product_id}`, params)
  );

export const getOrders = (params?: IOrdersParams) =>
  getAPIData(sendGet(`dropshipping/orders/`, params));

export const updateAddress = (params: IAddressParams) =>
  getAPIData(
    sendPut(`dropshipping/orders/update-address/${params.order_id}/`, params)
  );

export const createSampleOrder = (params: ICreateSampleOrderParams) =>
  getAPIData(
    sendPost(`dropshipping/sample-order/${params.variant_id}/`, params)
  );

export const deleteSampleOrder = (params: ICreateSampleOrderParams) =>
  getAPIData(sendDelete(`dropshipping/sample-order/${params.variant_id}/`, {}));

export const checkoutOrder = (sub_order_id: number) =>
  getAPIData(sendPost(`dropshipping/checkout/${sub_order_id}/`));

export const updateRetailPrice = (params: IUpdateRetailPriceParams) =>
  getAPIData(
    sendPut(
      `dropshipping/imported-products/update-variant/${params.variant_id}/`,
      params
    )
  );

export const getDropshippingSettings = () =>
  getAPIData(sendGet("dropshipping/settings/"));

export const cancelSubOrder = (sub_order_id: number) =>
  getAPIData(sendPost(`dropshipping/orders/cancel/${sub_order_id}/`));

export const getBulkCheckoutSummary = (
  subOrderIds: number[] = [],
  orderType: string
) =>
  getAPIData(
    sendGet(`dropshipping/bulk-checkout-summary/`, {
      ids: subOrderIds.join(","),
      order_type: orderType,
    })
  );

export const updateDropshippingSettings = (params: IDropshippingSettings) =>
  getAPIData(sendPost("dropshipping/settings/", params));

export const updateImportListBulk = (params: {
  products: IPushToStoreParams[];
}) => getAPIData(sendPut("dropshipping/imported-products/bulk/", params));

export const deleteImportedProductBulk = (params: { product_ids: string[] }) =>
  getAPIData(sendPost("dropshipping/imported-products/bulk/", params));

export const updateImportedProduct = (params: Partial<IProduct>) => {
  const { id, ...rest } = params;
  return getAPIData(sendPut(`dropshipping/imported-products/${id}/`, rest));
};

export const createProductAsset = (params: IProductAssetPayload) => {
  return getAPIData(sendPost("dropshipping/product-assets/", params));
};

export const updateProductAsset = (params: IProductAssetPayload) => {
  const { id, ...rest } = params;
  return getAPIData(sendPut(`dropshipping/product-assets/${id}`, rest));
};

export const deleteProductAsset = (assetId: number) => {
  return getAPIData(sendDelete(`dropshipping/product-assets/${assetId}`, {}));
};

export const bulkUpdateVariants = (params: IProductVariantsPayload) => {
  return getAPIData(sendPost("dropshipping/imported-variants/bulk/", params));
};

export const getProductsDatabase = (params?: any) => {
  const mapFilters = (filters: any) => {
    const mappedFilters: any = {};

    if (filters?.searchTerm?.trim()) {
      mappedFilters.search_term = filters.searchTerm.trim();
    }

    if (filters?.launchDateForShop?.startDate) {
      mappedFilters.launch_date_start = format(
        new Date(filters.launchDateForShop.startDate),
        "yyyy-MM-dd"
      );
    }

    if (filters?.launchDateForShop?.endDate) {
      mappedFilters.launch_date_end = format(
        new Date(filters.launchDateForShop.endDate),
        "yyyy-MM-dd"
      );
    }

    if (filters?.priceRange?.min) {
      mappedFilters.min_price = filters.priceRange.min * 100;
    }

    if (filters?.priceRange?.max) {
      mappedFilters.max_price = filters.priceRange.max * 100;
    }

    if (filters?.salesVolume?.min) {
      mappedFilters.min_sales = filters.salesVolume.min;
    }

    if (filters?.salesVolume?.max) {
      mappedFilters.max_sales = filters.salesVolume.max;
    }

    if (filters?.domainTLD) {
      mappedFilters.store_tld = filters.domainTLD;
    }

    if (filters?.storeCurrency) {
      mappedFilters.store_currency = filters.storeCurrency;
    }

    if (filters?.storeLanguage) {
      mappedFilters.store_language = filters.storeLanguage;
    }

    if (filters?.selectedCategories?.length) {
      mappedFilters.categories = filters.selectedCategories
        .reduce((acc: string, category: string) => {
          return acc + category + ",";
        }, "")
        .slice(0, -1);
    }

    if (filters?.keywordFilters?.includeTitleKeywords?.length) {
      mappedFilters.include_title = filters.keywordFilters.includeTitleKeywords
        .reduce((acc: string, keyword: string) => {
          return acc + keyword + ",";
        }, "")
        .slice(0, -1);
    }

    if (filters?.keywordFilters?.excludeTitleKeywords?.length) {
      mappedFilters.exclude_title = filters.keywordFilters.excludeTitleKeywords
        .reduce((acc: string, keyword: string) => {
          return acc + keyword + ",";
        }, "")
        .slice(0, -1);
    }

    if (filters?.keywordFilters?.includeDescriptionKeywords?.length) {
      mappedFilters.include_description =
        filters.keywordFilters.includeDescriptionKeywords
          .reduce((acc: string, keyword: string) => {
            return acc + keyword + ",";
          }, "")
          .slice(0, -1);
    }

    if (filters?.keywordFilters?.excludeDescriptionKeywords?.length) {
      mappedFilters.exclude_description =
        filters.keywordFilters.excludeDescriptionKeywords
          .reduce((acc: string, keyword: string) => {
            return acc + keyword + ",";
          }, "")
          .slice(0, -1);
    }
    return mappedFilters;
  };

  const { filters, ...restParams } = params;

  return getAPIData(
    sendGet("dropshipping/product-database/", {
      ...restParams,
      ...mapFilters(filters),
    })
  );
};

export const getProductsDetails = (product_id: number | null, params?: any) => {
  return getAPIData(
    sendGet(`dropshipping/products/${product_id}/details/`, params)
  );
};

export const trackProduct = (product_id: number) => {
  return getAPIData(sendPost(`dropshipping/products/${product_id}/track/`));
};

export const untrackProduct = (product_id: number) => {
  return getAPIData(
    sendDelete(`dropshipping/products/${product_id}/untrack/`, {})
  );
};

export const getDashboard = () => {
  return getAPIData(
    sendGet("dropshipping/dashboard/")
  )
}

export const importAliExpressProduct = (aeProductId: string) =>
  getAPIData(sendPost(`dropshipping/imported-ae-products/${aeProductId}/`));

export const pushAliExpressProductToStore = (aeProductId: string, payload: any) =>
  getAPIData(sendPut(`dropshipping/imported-ae-products/${aeProductId}/`, payload));